import React, { useEffect, useState } from "react";
import Add from "@mui/icons-material/Add";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { CardLoading } from "../core/CardLoading";
import { enqueueSnackbar } from "notistack";
import AddGameAwardFullScreenDialog from "./AddGameAwardFullScreenDialog";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { SmartTextField } from "./SmartTextField";
import { Buttons } from "../core/EditableListItemUrl";
import Stack from "@mui/material/Stack";
import { useEditGameAward, useGameAwards, useDeleteGameAward } from "@seabrookstudios/pitch2table";
import { s2Url } from "@seabrookstudios/types";
import Edit from "@mui/icons-material/Edit";
import ListItemButton from "@mui/material/ListItemButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Box from "@mui/material/Box";

/**
 * Edit Award Dialog Component
 */
const EditAwardDialog = ({ award, onUpdate, onDelete, children }) => {
  const [name, setName] = useState(award.name);
  const [url, setUrl] = useState(award.url || s2Url(""));
  const [year, setYear] = useState(award.year ? award.year.toString() : "");
  const [showOnGamePage, setShowOnGamePage] = useState(award.showOnGamePage);
  const [canSave, setCanSave] = useState(false);

  const onSaveHandler = async () => {
    await onUpdate({
      name,
      url,
      year: year.trim() !== "" ? parseInt(year, 10) : undefined,
      showOnDesignerPage: undefined,
      showOnGamePage,
    });
  };

  const onDeleteHandler = async () => {
    await onDelete();
  };

  useEffect(() => {
    setCanSave(!!(name && name.trim().length > 0));
  }, [name]);

  return (
    <FullScreenDialog
      title="Edit Award"
      actionText="save"
      onSaveHandler={onSaveHandler}
      onDeleteHandler={onDeleteHandler}
      canSave={!canSave}
      CustomControl={children}
    >
      <SmartTextField
        required
        label="Award Name"
        helperText="The name of the award"
        initialValue={name}
        onChange={setName}
      />
      <SmartTextField
        label="URL"
        helperText="The web address for more information about the award"
        initialValue={url}
        onChange={setUrl}
      />
      <SmartTextField
        label="Year"
        helperText="The year the award was received (e.g. 2023)"
        initialValue={year}
        onChange={setYear}
      />
      <Box mt={2}>
        <FormControlLabel
          control={<Checkbox checked={showOnGamePage} onChange={(e) => setShowOnGamePage(e.target.checked)} />}
          label="Show on public page"
        />
      </Box>
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").Awards} props.award
 */
const EditableAward = ({ gameId, award }) => {
  const onUpdateSuccess = () => {
    enqueueSnackbar("Award updated successfully", { variant: "success" });
  };

  const onUpdateError = () => {
    enqueueSnackbar("Failed to update award", { variant: "error" });
  };

  const onDeleteSuccess = () => {
    enqueueSnackbar("Award deleted successfully", { variant: "success" });
  };

  const onDeleteError = () => {
    enqueueSnackbar("Failed to delete award", { variant: "error" });
  };

  const updateGameAward = useEditGameAward(gameId, award.id, onUpdateSuccess, onUpdateError);
  const deleteGameAward = useDeleteGameAward(gameId, award.id, onDeleteSuccess, onDeleteError);

  const handleUpdateAward = async (awardData) => {
    await updateGameAward.mutateAsync(awardData);
  };

  const handleDeleteAward = async () => {
    await deleteGameAward.mutateAsync();
  };

  return (
    <Stack direction="column">
      <ListItem>
        <ListItemIcon>{award.year}</ListItemIcon>
        <ListItemText
          primary={award.name}
          secondary={award.showOnGamePage ? "Visible on public page" : "Hidden from public page"}
        />
        <EditAwardDialog award={award} onUpdate={handleUpdateAward} onDelete={handleDeleteAward}>
          <ListItemIcon>
            <ListItemButton>
              <Edit />
            </ListItemButton>
          </ListItemIcon>
        </EditAwardDialog>
        {award.url && <Buttons value={award.url} />}
      </ListItem>
    </Stack>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 */
export const GameAwardsCard = ({ gameId }) => {
  const { data: awards = [], isLoading } = useGameAwards(gameId);

  if (isLoading) {
    return <CardLoading />;
  }

  return (
    <Card elevation={1} square={false}>
      <List>
        <ListItem>
          <ListItemText primary="Awards" />
          <ListItemSecondaryAction>
            <AddGameAwardFullScreenDialog
              gameId={gameId}
              CustomControl={
                <IconButton color="primary" size="small">
                  <Add />
                </IconButton>
              }
            />
          </ListItemSecondaryAction>
        </ListItem>

        {awards.length === 0 ? (
          <ListItem>
            <ListItemText primary="No awards added yet" secondary="Add awards and recognition your game has received" />
          </ListItem>
        ) : (
          awards.map((award) => <EditableAward gameId={gameId} award={award} key={award.id} />)
        )}
      </List>
    </Card>
  );
};
